import { mixColors } from '@avrahami/contracts';
import { flyerAddress, splitTitle, textColorOn } from '../layout';
import {
  benefitIcon,
  bodyFont,
  displayFont,
  drawAmount,
  drawIcon,
  drawLines,
  drawLogo,
  drawPhoto,
  drawStruck,
  drawText,
  fillRect,
  fit,
  line,
  phoneOf,
  PHONE_ICON,
  readable,
  W,
  WHATSAPP_ICON,
  type Box,
  type Page,
} from '../kit';
import { GLOBE_PATHS } from '../icons';
import { clipBody, kickerOf, savingOf, tilesOf, WORDS, type Tile } from './shared';

/**
 * PANORAMA: steel and navy, a wide view. The title centred over a steel stage across the whole
 * page, the photo in its middle with the product's figures called out on either side like a
 * technical drawing, and a navy strip below in numbered columns, as the store's strip of
 * departments: the price, the sale, and the way to ask for a quote.
 */

const RIGHT = 1032;
const LEFT = 48;
const WHITE = '#ffffff';
const STAGE_BOTTOM = 812;
const PANEL = 826;

export function drawPanorama(page: Page) {
  const { ctx, flyer } = page;
  const { theme, storeDesign } = flyer;
  const ink = storeDesign.ink;
  const muted = mixColors(ink, WHITE, 0.42);
  const brand = readable(theme.brand, WHITE, [theme.brandDark, ink], 4);

  fillRect(ctx, { x: 0, y: 0, w: W, h: W }, WHITE);

  // The header: the logo, and the store's address.
  drawLogo(
    page,
    page.images.logo,
    flyer.store.name,
    { x: 540, y: 26, w: RIGHT - 540, h: 68 },
    {
      align: 'right',
      color: ink,
    },
  );
  if (flyer.store.website !== null) {
    drawIcon(ctx, GLOBE_PATHS, LEFT + 14, 60, 28, brand);
    drawText(ctx, clipBody(page, flyerAddress(flyer.store.website), 600, 22, 420), LEFT + 38, 68, {
      font: bodyFont(page, 600, 22),
      color: muted,
      align: 'left',
      direction: 'ltr',
    });
  }
  line(ctx, [0, 118], [W, 118], mixColors(ink, WHITE, 0.88), 1.5);

  // The title, centred, under a spaced eyebrow between two short rules.
  const eyebrow = clipBody(page, kickerOf(flyer), 700, 18, 700);
  ctx.font = bodyFont(page, 700, 18);
  ctx.letterSpacing = '4px';
  const eyebrowWidth = ctx.measureText(eyebrow).width;
  ctx.letterSpacing = '0px';
  drawText(ctx, eyebrow, W / 2, 168, {
    font: bodyFont(page, 700, 18),
    color: brand,
    align: 'center',
    spacing: 4,
  });
  const accentRule = readable(theme.accent, WHITE, [brand], 2);
  line(
    ctx,
    [W / 2 + eyebrowWidth / 2 + 18, 162],
    [W / 2 + eyebrowWidth / 2 + 70, 162],
    accentRule,
    3,
  );
  line(
    ctx,
    [W / 2 - eyebrowWidth / 2 - 18, 162],
    [W / 2 - eyebrowWidth / 2 - 70, 162],
    accentRule,
    3,
  );

  const [head, rest] = splitTitle(flyer.title);
  const headline = fit(page, head, 'display', {
    maxWidth: 940,
    maxLines: 1,
    maxSize: 92,
    minSize: 46,
    weight: 800,
  });
  let y = 186;
  y += drawLines(ctx, headline, {
    x: W / 2,
    y,
    font: displayFont(page, headline.size),
    color: ink,
    align: 'center',
    lineHeight: 1.05,
  });
  if (rest !== '') {
    const second = fit(page, rest, 'display', {
      maxWidth: 940,
      maxLines: 1,
      maxSize: Math.round(headline.size * 0.78),
      minSize: 34,
      weight: 800,
    });
    y += drawLines(ctx, second, {
      x: W / 2,
      y,
      font: displayFont(page, second.size),
      color: brand,
      align: 'center',
      lineHeight: 1.1,
    });
  }

  // The steel stage across the page, a floor under the product, and the photo.
  const stageTop = Math.max(y + 18, 330);
  const steel = ctx.createLinearGradient(0, stageTop, 0, STAGE_BOTTOM);
  steel.addColorStop(0, '#f8fafc');
  steel.addColorStop(1, mixColors(theme.brand, '#dfe5ec', 0.88));
  fillRect(ctx, { x: 0, y: stageTop, w: W, h: STAGE_BOTTOM - stageTop }, steel);
  const floorY = STAGE_BOTTOM - 46;
  const floor = ctx.createRadialGradient(W / 2, floorY, 0, W / 2, floorY, 260);
  floor.addColorStop(0, 'rgba(23, 27, 33, 0.16)');
  floor.addColorStop(1, 'rgba(23, 27, 33, 0)');
  ctx.save();
  ctx.translate(0, floorY);
  ctx.scale(1, 0.16);
  ctx.translate(0, -floorY);
  ctx.fillStyle = floor;
  ctx.fillRect(W / 2 - 280, floorY - 280, 560, 560);
  ctx.restore();
  drawPhoto(page, { x: 340, y: stageTop + 34, w: 400, h: STAGE_BOTTOM - stageTop - 84 }, 12);

  // The figures called out on either side.
  const callouts = tilesOf(flyer, 4);
  const rows = [stageTop + 46, stageTop + Math.max(170, (STAGE_BOTTOM - stageTop) / 2 + 20)];
  callouts.forEach((tile, index) => {
    const right = index % 2 === 0;
    const top = rows[Math.floor(index / 2)] ?? rows[0] ?? stageTop;
    drawCallout(page, tile, { x: right ? 800 : LEFT, y: top, w: 232, h: 120 }, right, brand);
  });

  // The navy strip, in numbered columns.
  const navy = mixColors(theme.brand, ink, 0.55);
  const onNavy = textColorOn(navy);
  const soft = mixColors(onNavy, navy, 0.35);
  const loud = readable(theme.accent, navy, [mixColors(theme.brand, WHITE, 0.6), onNavy], 3);
  fillRect(ctx, { x: 0, y: PANEL, w: W, h: W - PANEL }, navy);
  fillRect(ctx, { x: 0, y: PANEL, w: W, h: 6 }, readable(theme.accent, navy, [theme.brand], 1.5));
  const columns = [
    { right: RIGHT, left: 724 },
    { right: 692, left: 404 },
    { right: 372, left: LEFT },
  ];
  for (const at of [708, 388])
    line(ctx, [at, PANEL + 44], [at, W - 36], mixColors(onNavy, navy, 0.8), 1.5);
  const head1 = (index: number, text: string, column: { right: number }) => {
    drawText(ctx, `0${String(index)}`, column.right, PANEL + 64, {
      font: bodyFont(page, 700, 17),
      color: loud,
      direction: 'ltr',
      spacing: 2,
    });
    drawText(ctx, text, column.right - 40, PANEL + 64, {
      font: bodyFont(page, 600, 18),
      color: soft,
    });
  };
  const [first, middle, last] = columns as [
    (typeof columns)[0],
    (typeof columns)[0],
    (typeof columns)[0],
  ];

  const { price } = flyer;
  const saving = savingOf(flyer);
  if (price !== null) {
    head1(1, saving === null ? WORDS.price : WORDS.salePrice, first);
    drawAmount(page, price.amountMinor, {
      x: first.right,
      baseline: PANEL + 168,
      size: 84,
      color: loud,
      maxWidth: first.right - first.left,
    });
  } else {
    head1(1, WORDS.price, first);
    drawText(ctx, WORDS.quote, first.right, PANEL + 150, {
      font: displayFont(page, 44),
      color: onNavy,
      maxWidth: first.right - first.left,
    });
    drawText(ctx, WORDS.noCommitment, first.right, PANEL + 196, {
      font: bodyFont(page, 500, 20),
      color: soft,
    });
  }

  if (price !== null && saving !== null && price.compareAtMinor !== null) {
    head1(2, WORDS.regular, middle);
    drawStruck(page, price.compareAtMinor, {
      x: middle.right,
      baseline: PANEL + 130,
      font: displayFont(page, 40),
      color: soft,
      strike: loud,
    });
    const said = drawText(ctx, WORDS.saving, middle.right, PANEL + 196, {
      font: bodyFont(page, 600, 22),
      color: onNavy,
    });
    drawAmount(page, saving, {
      x: middle.right - said - 12,
      baseline: PANEL + 198,
      size: 40,
      color: loud,
      maxWidth: middle.right - middle.left - said - 12,
    });
  } else {
    // No sale: the strengths the callouts did not take.
    const shown = callouts.filter((tile) => tile.kind === 'BENEFIT').length;
    const benefits = flyer.benefits.slice(shown, shown + 2);
    head1(2, benefits.length > 0 ? 'בקצרה' : 'החנות', middle);
    if (benefits.length === 0) {
      const where =
        flyer.store.website === null ? flyer.store.name : flyerAddress(flyer.store.website);
      drawText(
        ctx,
        clipBody(page, where, 700, 26, middle.right - middle.left),
        middle.right,
        PANEL + 150,
        {
          font: bodyFont(page, 700, 26),
          color: onNavy,
          direction: flyer.store.website === null ? 'rtl' : 'ltr',
        },
      );
    }
    benefits.forEach((benefit, index) => {
      const top = PANEL + 96 + index * 64;
      drawIcon(ctx, benefitIcon(benefit.icon), middle.right - 16, top + 18, 30, loud, 2.2);
      const text = fit(page, benefit.title, 'body', {
        maxWidth: middle.right - middle.left - 46,
        maxLines: 2,
        maxSize: 20,
        minSize: 16,
        weight: 600,
      });
      drawLines(ctx, text, {
        x: middle.right - 44,
        y: top,
        font: bodyFont(page, 600, text.size),
        color: onNavy,
        lineHeight: 1.2,
      });
    });
  }

  head1(3, 'הצעת מחיר', last);
  drawText(ctx, WORDS.cta, last.right, PANEL + 124, {
    font: bodyFont(page, 800, 27),
    color: onNavy,
  });
  const phone = phoneOf(flyer);
  if (phone !== null) {
    drawText(
      ctx,
      clipBody(page, phone, 800, 34, last.right - last.left - 60),
      last.right,
      PANEL + 186,
      {
        font: displayFont(page, 42),
        color: onNavy,
        direction: 'ltr',
      },
    );
  }
  drawIcon(
    ctx,
    flyer.store.whatsapp === null ? PHONE_ICON : WHATSAPP_ICON,
    last.left + 22,
    PANEL + 172,
    36,
    loud,
    2.2,
  );
}

/** One figure or strength, with a leader line and a dot reaching toward the product. */
function drawCallout(page: Page, tile: Tile, box: Box, onRight: boolean, brand: string) {
  const { ctx, flyer } = page;
  const ink = flyer.storeDesign.ink;
  const muted = mixColors(ink, WHITE, 0.42);
  // The text keeps to the photo's side of its box; the leader runs from it to the photo.
  const edge = onRight ? box.x : box.x + box.w;
  const align: CanvasTextAlign = onRight ? 'left' : 'right';
  const leaderFrom = onRight ? box.x - 10 : box.x + box.w + 10;
  const leaderTo = onRight ? box.x - 64 : box.x + box.w + 64;
  const leaderY = box.y + 30;
  line(ctx, [leaderFrom, leaderY], [leaderTo, leaderY], brand, 1.5);
  ctx.fillStyle = WHITE;
  ctx.beginPath();
  ctx.arc(leaderTo, leaderY, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = brand;
  ctx.beginPath();
  ctx.arc(leaderTo, leaderY, 5, 0, Math.PI * 2);
  ctx.fill();

  if (tile.kind === 'FIGURE') {
    const { value, unit, label } = tile.figure;
    const unitFont = bodyFont(page, 700, 20);
    ctx.font = unitFont;
    const unitWidth = unit === '' ? 0 : ctx.measureText(unit).width + 4;
    const big = fit(page, value, 'display', {
      maxWidth: box.w - unitWidth,
      maxLines: 1,
      maxSize: 54,
      minSize: 26,
      weight: 800,
    });
    ctx.font = displayFont(page, big.size);
    const valueWidth = ctx.measureText(big.lines[0] ?? '').width;
    // Left to right: the number, then its unit.
    const start = onRight ? edge : edge - valueWidth - unitWidth;
    drawText(ctx, big.lines[0] ?? '', start, box.y + 48, {
      font: displayFont(page, big.size),
      color: ink,
      align: 'left',
      direction: 'ltr',
    });
    if (unit !== '') {
      drawText(ctx, unit, start + valueWidth + 4, box.y + 48, {
        font: unitFont,
        color: brand,
        align: 'left',
        direction: 'ltr',
      });
    }
    const caption = fit(page, label, 'body', {
      maxWidth: box.w,
      maxLines: 2,
      maxSize: 18,
      minSize: 15,
      weight: 600,
    });
    drawLines(ctx, caption, {
      x: edge,
      y: box.y + 62,
      font: bodyFont(page, 600, caption.size),
      color: muted,
      align,
      lineHeight: 1.25,
    });
  } else {
    const iconX = onRight ? edge + 16 : edge - 16;
    drawIcon(ctx, benefitIcon(tile.icon), iconX, leaderY, 32, brand, 2.2);
    const caption = fit(page, tile.title, 'body', {
      maxWidth: box.w,
      maxLines: 3,
      maxSize: 20,
      minSize: 15,
      weight: 700,
    });
    drawLines(ctx, caption, {
      x: edge,
      y: box.y + 56,
      font: bodyFont(page, 700, caption.size),
      color: ink,
      align,
      lineHeight: 1.25,
    });
  }
}
